import React from 'react'
import TeamIcon from '../users/TeamIcon'
import StreamName from '../filters/StreamName'
import useStore from '../../state/knovStore'
import { useAvailableTeams } from '../filters/useStreamFilters'
import { ITeam } from 'state/knovStore'
import cn from 'classnames'
import styles from './stream-embed.module.scss'
import { v4 as uuid } from 'uuid'
import useInsertRight from 'refactor/hooks/useInsertRight'
import usePanelContext from 'refactor/hooks/usePanelContext'
import { Waves } from 'lucide-react'

interface StreamEmbedProps {
    streamId?: string
    stream?: ITeam
    // Stream can be a team stream or other types
    filter?: any
    className?: string
    onMouseEnter?: (ev: React.MouseEvent) => void
    onMouseLeave?: (ev: React.MouseEvent) => void
    // Metadata props
    lastActivity?: string
    questCount?: number
}

const StreamEmbed: React.FC<StreamEmbedProps> = ({
    streamId,
    stream,
    filter: providedFilter,
    className,
    onMouseEnter,
    onMouseLeave,
    lastActivity,
    questCount,
}) => {
    const currentUser = gon.currentUser
    const teams = useAvailableTeams(currentUser)
    const { insertRight } = useInsertRight()
    const { panelToRight } = usePanelContext()

    // Use provided stream or find the team based on streamId
    const team = stream || (streamId ? teams.find(t => t.id === streamId) : null)

    // State for banner
    const [hasBanner, setHasBanner] = React.useState(false)
    const [bannerChecked, setBannerChecked] = React.useState(false)
    const [bannerKey] = React.useState(Date.now())

    // Determine the filter for StreamName component
    let filter = providedFilter
    if (!filter && (streamId || team?.id)) {
        filter = { team_ids: [streamId || team.id] }
    }

    if (!team || !filter) {
        return null
    }

    const teamId = team.id

    const onClick = async (ev: React.MouseEvent) => {
        ev.preventDefault()
        ev.stopPropagation()

        const rightFilter = panelToRight()?.filter
        const rightTeamIds = rightFilter?.team_ids || []

        if (rightTeamIds.includes(teamId) && rightTeamIds.length === 1) {
            panelToRight()?.shake()
        } else {
            const rightPanelId = uuid()

            // Check if the team ID matches Twetch or Hodlocker and set appropriate filter
            let filterToUse = filter
            if (teamId === gon.TWETCH_TEAM_ID) {
                filterToUse = { twetch: true }
            } else if (teamId === gon.HODLOCKER_TEAM_ID) {
                filterToUse = { hodlocker: true }
            }

            insertRight({ panelId: rightPanelId, filter: filterToUse })
        }
    }

    const handleMouseEnter = (ev: React.MouseEvent) => {
        // Here we do the opposite entry function bc entering this component is equivalent to leaving the parent comp.
        if (onMouseLeave) onMouseLeave(ev)
    }

    const handleMouseLeave = (ev: React.MouseEvent) => {
        // Here we do the opposite entry function bc leaving this component is equivalent to entering the parent comp.
        if (onMouseEnter) onMouseEnter(ev)
    }

    return (
        <div
            className={cn(styles.streamEmbed, className, team && styles.withBanner)}
            onClick={onClick}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            {team && (
                <div className={hasBanner ? styles.bannerContainer : styles.bannerPlaceholder}>
                    {hasBanner ? (
                        <img
                            key={`banner-${bannerKey}`}
                            src={`${window.knovApiUrl}/api/v1/teams/${team.id}/banner?size=small&t=${bannerKey}`}
                            alt=""
                            className={styles.banner}
                        />
                    ) : (
                        <Waves className={styles.wavesIcon} size={32} />
                    )}
                </div>
            )}
            {team && (
                <img
                    key={`banner-check-${bannerKey}`}
                    src={`${window.knovApiUrl}/api/v1/teams/${team.id}/banner?size=small&t=${bannerKey}`}
                    onError={() => {
                        setHasBanner(false)
                        setBannerChecked(true)
                    }}
                    onLoad={() => {
                        setHasBanner(true)
                        setBannerChecked(true)
                    }}
                    style={{ display: 'none' }}
                    alt=""
                />
            )}
            {team && <div className={styles.streamLabel}>STREAM</div>}
            <div className={styles.streamHeader}>
                <div className={styles.streamIcon}>
                    <TeamIcon
                        team={team}
                        type="embed"
                        contextStyles={styles.filterIcon}
                        tip={`${team.name} Stream`}
                        borderStyles={styles.teamIconBorder}
                        initialStyles={styles.teamIconInitial}
                    />
                </div>
                <div className={styles.streamName}>
                    <StreamName filter={filter} contextStyles={styles.streamNameText} />
                </div>
            </div>
            {(lastActivity || questCount !== undefined) && (
                <div className={styles.streamMeta}>
                    {lastActivity && (
                        <span className={styles.metaItem}>
                            <i className="fa fa-clock-o" />
                            {lastActivity}
                        </span>
                    )}
                    {questCount !== undefined && (
                        <span className={styles.metaItem}>
                            {questCount} thread{questCount !== 1 ? 's' : ''}
                        </span>
                    )}
                </div>
            )}
        </div>
    )
}

export default StreamEmbed
